import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import wordmark from "@/assets/backland-wordmark.svg";
import pathHero from "@/assets/path-hero.jpg.asset.json";
import { listExperiences } from "@/lib/experiences.functions";


// Kept exported for backward-compat with existing imports; experiences are
// now driven by the CMS so this is just a plain string slug.
export type ExperienceSlug = string;

const PRESS = [
  "Condé Nast",
  "Travel + Leisure",
  "Forbes",
  "Inside Outdoor",
  "Fodor's Travel",
];

interface Props {
  onPick: (slug: ExperienceSlug) => void;
  errorMessage?: string;
  inline?: boolean;
  selectedSlug?: ExperienceSlug | null;
  isActive?: boolean;
}

export default function ExperienceChooser({
  onPick,
  errorMessage,
  inline = false,
  selectedSlug = null,
  isActive = true,
}: Props) {
  const fetchExps = useServerFn(listExperiences);
  const { data: allExps } = useQuery({
    queryKey: ["experiences"],
    queryFn: () => fetchExps(),
    staleTime: 60_000,
  });
  // Static fallback so the LCP shell paints immediately during SSR /
  // first client render, before the experiences query resolves. The DB
  // result hydrates over this once it lands.
  const FALLBACK_EXPERIENCES = [
    { slug: "couples", label: "Couples", tagline: "Romance & retreat", include_in_picker: true },
    { slug: "family", label: "Family", tagline: "Adventure together", include_in_picker: true },
  ];
  const experiences = allExps
    ? allExps.filter((e) => e.include_in_picker)
    : FALLBACK_EXPERIENCES;
  const [leaving, setLeaving] = useState<ExperienceSlug | null>(null);




  const handlePick = (slug: ExperienceSlug) => {
    try {
      localStorage.setItem("backland.experience", slug);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.setItem("backland:introDismissed", "1");
    } catch {
      /* ignore */
    }
    if (inline) {
      onPick(slug);
      return;
    }
    setLeaving(slug);
    window.setTimeout(() => onPick(slug), 260);
  };

  const positionClass = inline
    ? "absolute inset-0"
    : `fixed inset-0 z-50 transition-opacity duration-300 ${
        leaving ? "opacity-0" : "opacity-100"
      }`;

  return (
    <div
      className={`${positionClass} flex flex-col bg-background text-foreground overflow-hidden`}
    >
      {/* Background visual layer */}
      <div className="absolute inset-0 z-0">
        <img
          src={pathHero.url}
          alt=""
          aria-hidden
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/25 to-black/90" />
      </div>

      {/* Content layer */}
      <div className="relative z-10 h-full flex flex-col px-6 pt-[max(env(safe-area-inset-top),2rem)] pb-[calc(84px+env(safe-area-inset-bottom)+1rem)]">
        {/* TOP — wordmark */}
        <header className="text-center">
          <div className="space-y-2 flex flex-col items-center">
            {isActive ? (
              <h1 className="sr-only">Backland Luxury Nature Resort</h1>
            ) : (
              <div className="sr-only">Backland Luxury Nature Resort</div>
            )}
            <img
              src={wordmark}
              alt="Backland Luxury Nature Resort"
              width={302}
              height={55}
              fetchPriority="high"
              decoding="async"
              className="h-9 md:h-11 w-auto"
              draggable={false}
            />
            <p className="font-serif italic text-foreground/85 text-base md:text-lg leading-none fade-up">
              luxury nature resort
            </p>
          </div>

          {errorMessage && (
            <div className="mx-auto max-w-sm mt-5 rounded-md border border-border/40 bg-black/40 backdrop-blur-sm px-4 py-2.5 text-xs text-foreground/75">
              {errorMessage}
            </div>
          )}
        </header>

        {/* MIDDLE — prompt + cards (visual center) */}
        <div className="flex-1 flex flex-col justify-center py-8">
          <div className="space-y-7 fade-up">
            <div className="text-center space-y-2 max-w-xl mx-auto">
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-foreground leading-tight">
                How would you like to experience Backland?
              </h2>
              <p className="text-foreground/75 text-sm md:text-base">
                Choose a path to personalize your visit.
              </p>
            </div>

            <div
              className={`grid gap-3 mx-auto w-full ${
                experiences.length === 1
                  ? "grid-cols-1 max-w-sm"
                  : experiences.length === 2
                  ? "grid-cols-1 sm:grid-cols-2 max-w-md sm:max-w-lg"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-md sm:max-w-lg lg:max-w-3xl"
              }`}
            >
              {experiences.map((exp, idx) => {
                const isSelected = selectedSlug === exp.slug;
                const hasSelection = !!selectedSlug;
                const isDimmed = hasSelection && !isSelected;
                const isOrphan =
                  experiences.length === 3 && idx === 2;
                return (
                  <button
                    key={exp.slug}
                    type="button"
                    onClick={() => handlePick(exp.slug)}
                    aria-label={`Choose ${exp.label} experience`}
                    aria-pressed={isSelected}
                    className={`chooser-card group relative flex flex-col items-center justify-center gap-1.5 py-5 px-4 rounded-xl border backdrop-blur-md transition-all active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-gold/50 ${
                      isOrphan
                        ? "sm:col-span-2 sm:justify-self-center sm:w-[calc(50%-0.375rem)] lg:col-span-1 lg:w-full lg:justify-self-auto"
                        : ""
                    } ${
                      isSelected
                        ? "border-gold/60 bg-white/10 shadow-[0_0_24px_-6px_rgba(212,175,90,0.45)]"
                        : "border-gold/25 bg-white/[0.04] hover:bg-white/10 hover:border-gold/60"
                    } ${isDimmed ? "opacity-55" : "opacity-100"}`}
                  >
                    <span
                      className={`font-serif text-xl md:text-2xl leading-none transition-colors ${
                        isSelected ? "text-gold" : "text-foreground group-hover:text-gold"
                      }`}
                    >
                      {exp.label}
                    </span>
                    {exp.tagline && (
                      <span className="text-[10px] md:text-[11px] font-light tracking-[0.18em] uppercase text-foreground/65">
                        {exp.tagline}
                      </span>
                    )}
                    <span className="mt-1 text-[10px] tracking-[0.2em] uppercase text-gold/90 font-medium">
                      Select →
                    </span>
                    {isSelected && (
                      <span className="absolute -top-1.5 right-3 text-[8px] tracking-[0.22em] uppercase text-background bg-gold px-2 py-0.5 rounded-full font-medium">
                        Selected
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <p className="text-center text-foreground/60 text-xs md:text-sm italic max-w-md mx-auto">
              Change anytime by revisiting the PATH section.
            </p>
          </div>
        </div>

        {/* BOTTOM — press strip (de-emphasized) */}
        <div className="fade-up">
          <div className="flex items-center gap-3 max-w-xs mx-auto mb-2">
            <div className="h-px flex-1 bg-foreground/10" />
            <span className="text-[10px] md:text-[11px] text-foreground/50 uppercase tracking-[0.28em] whitespace-nowrap">
              FEATURED IN
            </span>
            <div className="h-px flex-1 bg-foreground/10" />
          </div>
          <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-1.5 px-2">
            {PRESS.map((p) => (
              <span
                key={p}
                className="text-[10px] md:text-xs font-semibold tracking-[0.12em] uppercase text-foreground/65"
              >
                {p}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
